const TARGET_RUNTIME_PROFILE_PATHS = new Set([
  "/api/shell",
  "/api/dashboard",
  "/api/dashboard/home",
  "/api/results/latest",
  "/api/data-manager/state",
  "/api/import",
  "/api/me/access",
]);

export const RUNTIME_REQUEST_PROFILE_EVENT = "grain:runtime-request-profile";

const runtimeRequestProfiles = [];
const MAX_RUNTIME_REQUEST_PROFILES = 100;

function resolveBaseUrl() {
  if (typeof window !== "undefined" && window.location?.origin) {
    return window.location.origin;
  }

  return "https://grain.local";
}

function toResolvedUrl(url) {
  if (typeof url !== "string" || !url.trim()) {
    return null;
  }

  try {
    return new URL(url, resolveBaseUrl());
  } catch {
    return null;
  }
}

function cloneRuntimeProfilePayload(value) {
  if (!value || typeof value !== "object") {
    return value ?? null;
  }

  return JSON.parse(JSON.stringify(value));
}

function normalizeDurationMs(value) {
  if (!Number.isFinite(value) || value < 0) {
    return null;
  }

  return Math.round(value * 10) / 10;
}

function normalizeOptionalByteCount(value) {
  if (!Number.isFinite(value) || value < 0) {
    return null;
  }

  return Math.round(value);
}

export function isRuntimeProfileTarget(url) {
  const resolvedUrl = toResolvedUrl(url);
  return Boolean(resolvedUrl && TARGET_RUNTIME_PROFILE_PATHS.has(resolvedUrl.pathname));
}

export function clearRuntimeRequestProfiles() {
  runtimeRequestProfiles.length = 0;
}

export function readRuntimeRequestProfiles() {
  return runtimeRequestProfiles.map((entry) => ({
    ...entry,
    runtimeProfile: cloneRuntimeProfilePayload(entry.runtimeProfile),
  }));
}

export function parseRuntimeProfileHeader(value) {
  if (typeof value !== "string" || !value.trim()) {
    return null;
  }

  try {
    const parsedValue = JSON.parse(value);
    return parsedValue && typeof parsedValue === "object" ? parsedValue : null;
  } catch {
    return null;
  }
}

function publishRuntimeRequestProfile(entry) {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.__grainRuntimeRequestProfiles = readRuntimeRequestProfiles();
  } catch {}

  if (typeof window.dispatchEvent !== "function" || typeof CustomEvent !== "function") {
    return;
  }

  try {
    window.dispatchEvent(new CustomEvent(RUNTIME_REQUEST_PROFILE_EVENT, { detail: entry }));
  } catch {}
}

export function recordRuntimeRequestProfile({
  url,
  method = "GET",
  durationMs = null,
  requestBodyBytes = null,
  response = null,
  error = null,
} = {}) {
  const resolvedUrl = toResolvedUrl(url);
  if (!resolvedUrl || !TARGET_RUNTIME_PROFILE_PATHS.has(resolvedUrl.pathname)) {
    return null;
  }

  const runtimeProfile = parseRuntimeProfileHeader(response?.headers?.get("x-grain-run-diagnostics") || "");
  const responseBodyBytes = Number.parseInt(response?.headers?.get("content-length") || "", 10);
  const recordedAtEpochMs = Date.now();
  const entry = {
    durationMs: normalizeDurationMs(durationMs),
    errorMessage: error instanceof Error && error.message ? error.message : null,
    method: typeof method === "string" && method.trim() ? method.trim().toUpperCase() : "GET",
    ok: response?.ok === true,
    path: resolvedUrl.pathname,
    recordedAt: new Date(recordedAtEpochMs).toISOString(),
    recordedAtEpochMs,
    requestBodyBytes: normalizeOptionalByteCount(requestBodyBytes),
    responseBodyBytes: normalizeOptionalByteCount(responseBodyBytes),
    runtimeProfile,
    serverTiming: response?.headers?.get("server-timing") || "",
    status: typeof response?.status === "number" ? response.status : null,
    url: `${resolvedUrl.pathname}${resolvedUrl.search}${resolvedUrl.hash}`,
  };

  runtimeRequestProfiles.push(entry);
  if (runtimeRequestProfiles.length > MAX_RUNTIME_REQUEST_PROFILES) {
    runtimeRequestProfiles.splice(0, runtimeRequestProfiles.length - MAX_RUNTIME_REQUEST_PROFILES);
  }

  publishRuntimeRequestProfile(entry);
  return entry;
}
